import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { useGoogleMapsLoader } from "@/hooks/useGoogleMapsLoader.web";
import { useThemeColor } from "@/hooks/useThemeColor";
import { requestBrowserPosition } from "@/utils/geo.web";
import { Circle, GoogleMap, Marker } from "@react-google-maps/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";
import { createPortal } from "react-dom";

type MyPosition = { lat: number; lng: number; accuracy?: number };

const MY_LOCATION_BLUE = "#1a73e8";

export type LocationValue = {
  latitude: number;
  longitude: number;
  city?: string;
  country?: string;
} | null;

export interface LocationPickerProps {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
  readOnly?: boolean;
  label?: string;
  defaultCenter?: { latitude: number; longitude: number };
}

const reverseGeocode = async (
  latitude: number,
  longitude: number,
): Promise<{ city: string; country: string }> => {
  try {
    if (typeof window === "undefined" || !window.google?.maps) {
      return { city: "", country: "" };
    }
    const geocoder = new window.google.maps.Geocoder();
    const response = await geocoder.geocode({
      location: { lat: latitude, lng: longitude },
    });
    if (response.results && response.results.length > 0) {
      const components = response.results[0].address_components;
      const cityComponent = components.find(
        (c) =>
          c.types.includes("locality") ||
          c.types.includes("administrative_area_level_1"),
      );
      const countryComponent = components.find((c) => c.types.includes("country"));
      return {
        city: cityComponent?.long_name || "",
        country: countryComponent?.short_name || "",
      };
    }
  } catch (e) {
    console.warn("Reverse geocoding failed", e);
  }
  return { city: "", country: "" };
};

export function LocationPicker({
  value,
  onChange,
  readOnly,
  label,
  defaultCenter,
}: LocationPickerProps) {
  const { t } = useTranslation();
  const [mapVisible, setMapVisible] = useState(false);
  const [opening, setOpening] = useState(false);
  const [myPos, setMyPos] = useState<MyPosition | null>(null);
  const stripes = useThemeColor({}, "stripes");
  const tint = useThemeColor({}, "tint");
  const lastGeocodedRef = useRef<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useGoogleMapsLoader();

  const refreshMyPos = useCallback(async (): Promise<MyPosition | null> => {
    const result = await requestBrowserPosition();
    if (result.kind !== "ok") {
      setMyPos(null);
      return null;
    }
    const next = { lat: result.position.lat, lng: result.position.lng };
    setMyPos(next);
    return next;
  }, []);

  const handleRecenter = useCallback(async () => {
    const next = (await refreshMyPos()) ?? myPos;
    if (next && mapRef.current) {
      mapRef.current.panTo({ lat: next.lat, lng: next.lng });
    }
  }, [refreshMyPos, myPos]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (!mapVisible) return;
    void refreshMyPos();
  }, [mapVisible, refreshMyPos]);

  useEffect(() => {
    if (!value || !isLoaded) {
      if (!value) lastGeocodedRef.current = null;
      return;
    }
    const key = `${value.latitude},${value.longitude}`;
    if (lastGeocodedRef.current === key) return;
    if (value.city || value.country) {
      lastGeocodedRef.current = key;
      return;
    }
    lastGeocodedRef.current = key;
    (async () => {
      const { city, country } = await reverseGeocode(
        value.latitude,
        value.longitude,
      );
      if (!city && !country) return;
      onChange({
        latitude: value.latitude,
        longitude: value.longitude,
        city,
        country,
      });
    })();
  }, [value, isLoaded, onChange]);

  const setCoordsFromEvent = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      onChange({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
    },
    [onChange],
  );

  const handleMouseDown = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (readOnly) return;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      longPressTimerRef.current = setTimeout(() => {
        setCoordsFromEvent(e);
      }, 500);
    },
    [readOnly, setCoordsFromEvent],
  );

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleOpen = async () => {
    if (opening) return;
    setOpening(true);
    try {
      if (!value) {
        let coords: { latitude: number; longitude: number } | null = null;
        const result = await requestBrowserPosition();
        if (result.kind === "ok") {
          coords = {
            latitude: result.position.lat,
            longitude: result.position.lng,
          };
          setMyPos({ lat: result.position.lat, lng: result.position.lng });
        }
        if (!coords && defaultCenter) {
          coords = defaultCenter;
        }
        if (coords) {
          onChange({ latitude: coords.latitude, longitude: coords.longitude });
        }
      }
      setMapVisible(true);
    } finally {
      setOpening(false);
    }
  };

  const locationStr = value
    ? value.city && value.country
      ? `${value.city}, ${value.country}`
      : value.city ||
        value.country ||
        `${value.latitude.toFixed(4)}, ${value.longitude.toFixed(4)}`
    : "";

  const previewCenter = value
    ? { lat: value.latitude, lng: value.longitude }
    : { lat: 0, lng: 0 };

  return (
    <ThemedView style={styles.container}>
      {!!label && <ThemedText style={styles.label}>{label}</ThemedText>}
      {value && (
        <>
          <div
            onClick={() => setMapVisible(true)}
            style={{
              height: 140,
              borderRadius: 8,
              overflow: "hidden",
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: stripes,
              marginBottom: 8,
              cursor: "pointer",
            }}
            data-testid="location-preview"
          >
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={previewCenter}
                zoom={14}
                options={{
                  gestureHandling: "none",
                  disableDefaultUI: true,
                  clickableIcons: false,
                  keyboardShortcuts: false,
                }}
              >
                <Marker position={previewCenter} />
              </GoogleMap>
            ) : (
              <ActivityIndicator color={tint} />
            )}
          </div>
          {!!locationStr && (
            <ThemedText style={styles.locationText}>{locationStr}</ThemedText>
          )}
          {readOnly && (
            <ThemedText style={styles.locked}>
              {t("LOCATION_LOCKED_BY_GROUP")}
            </ThemedText>
          )}
        </>
      )}
      {!readOnly && (
        <View style={styles.actions}>
          {opening ? (
            <ActivityIndicator color={tint} />
          ) : (
            <TouchableOpacity
              onPress={handleOpen}
              accessibilityRole="button"
              activeOpacity={0.7}
              style={styles.linkButton}
            >
              <IconSymbol name="location" size={16} color="#1e3a8a" />
              <ThemedText style={styles.linkText}>
                {value ? t("EDIT_LOCATION") : t("ADD_LOCATION")}
              </ThemedText>
            </TouchableOpacity>
          )}
          {value && (
            <TouchableOpacity
              onPress={() => onChange(null)}
              accessibilityRole="button"
              activeOpacity={0.7}
              style={styles.linkButton}
            >
              <IconSymbol name="xmark.circle.fill" size={16} color="#b50000" />
              <ThemedText style={[styles.linkText, styles.removeText]}>
                {t("REMOVE_LOCATION")}
              </ThemedText>
            </TouchableOpacity>
          )}
        </View>
      )}
      {mapVisible &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            data-testid="location-modal"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <ThemedView style={styles.modalContainer}>
              <View style={styles.modalMap}>
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={previewCenter}
                    zoom={14}
                    onLoad={handleMapLoad}
                    onUnmount={handleMapUnmount}
                    onDblClick={readOnly ? undefined : setCoordsFromEvent}
                    onMouseDown={readOnly ? undefined : handleMouseDown}
                    onMouseUp={readOnly ? undefined : cancelLongPress}
                    onDragStart={readOnly ? undefined : cancelLongPress}
                    options={{
                      gestureHandling: readOnly ? "cooperative" : "greedy",
                      disableDoubleClickZoom: !readOnly,
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: false,
                      zoomControl: false,
                      cameraControl: false,
                    }}
                  >
                    {value && <Marker position={previewCenter} />}
                    {myPos && (
                      <>
                        <Circle
                          center={{ lat: myPos.lat, lng: myPos.lng }}
                          radius={30}
                          options={{
                            fillColor: MY_LOCATION_BLUE,
                            fillOpacity: 0.15,
                            strokeOpacity: 0,
                            clickable: false,
                            zIndex: 1,
                          }}
                        />
                        <Circle
                          center={{ lat: myPos.lat, lng: myPos.lng }}
                          radius={6}
                          options={{
                            fillColor: MY_LOCATION_BLUE,
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            clickable: false,
                            zIndex: 2,
                          }}
                        />
                      </>
                    )}
                  </GoogleMap>
                ) : (
                  <ActivityIndicator color={tint} />
                )}
                {isLoaded && myPos && (
                  <button
                    type="button"
                    aria-label={t("MY_LOCATION")}
                    data-testid="recenter-button"
                    onClick={handleRecenter}
                    style={{
                      position: "absolute",
                      bottom: 24,
                      right: 10,
                      width: 44,
                      height: 44,
                      borderRadius: 22,
                      border: "none",
                      background: "#ffffff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                    }}
                  >
                    <IconSymbol
                      name="location.fill"
                      size={22}
                      color={MY_LOCATION_BLUE}
                    />
                  </button>
                )}
              </View>
              <Button
                text={t("CLOSE_MAP")}
                onPress={() => setMapVisible(false)}
                style={styles.closeButton}
              />
            </ThemedView>
          </div>,
          document.body,
        )}
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: { marginBottom: 16 },
  label: { fontSize: 16, marginBottom: 8 },
  preview: {
    height: 140,
    borderRadius: 8,
    overflow: "hidden",
    borderWidth: 1,
    marginBottom: 8,
  },
  locationText: { fontSize: 14, marginBottom: 8 },
  locked: { fontSize: 12, fontStyle: "italic", marginBottom: 8, opacity: 0.7 },
  actions: { flexDirection: "row", alignItems: "center", gap: 16 },
  linkButton: { flexDirection: "row", alignItems: "center", gap: 4 },
  linkText: { color: "#1e3a8a", fontWeight: "700", fontSize: 14 },
  removeText: { color: "#b50000" },
  modalContainer: {
    width: "90%",
    height: "80%",
    maxWidth: 720,
    borderRadius: 12,
    overflow: "hidden",
    padding: 12,
  },
  modalMap: {
    flex: 1,
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 8,
    position: "relative",
  },
  closeButton: { width: "100%", maxWidth: "100%" },
});
